'use client';

import { cn } from '../../lib/utils';
import type { Reaction, User } from '../../types/comments';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

interface ReactionDisplayProps {
  reactions: Reaction[];
  currentUserId: number;
  users: User[];
  onReaction: (emoji: string) => void;
}

export function ReactionDisplay({
  reactions,
  currentUserId,
  users,
  onReaction,
}: ReactionDisplayProps) {
  const groupedReactions = (reactions || []).reduce(
    (acc, r) => {
      if (!acc[r.emoji]) acc[r.emoji] = [];
      acc[r.emoji].push(r.userId);
      return acc;
    },
    {} as Record<string, number[]>
  );

  const reactionEntries = Object.entries(groupedReactions);
  if (reactionEntries.length === 0) return null;

  return (
    <TooltipProvider>
      <div className="flex flex-wrap gap-1">
        {reactionEntries.map(([emoji, userIds]) => {
          const hasReacted = userIds.includes(currentUserId);
          const reactingUsers = userIds
            .map((id) => users.find((u) => u.id === id))
            .filter((u): u is User => !!u);
          const userNames = reactingUsers.map((u) => u.fullName || 'Someone').join(', ');

          return (
            <Tooltip key={emoji}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReaction(emoji)}
                  className={cn(
                    'h-6 gap-1 px-1.5 text-xs',
                    hasReacted && 'border-primary/50 bg-primary/10'
                  )}
                >
                  <span>{emoji}</span>
                  <span className="text-muted-foreground">{userIds.length}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {userNames}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
